import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { brl } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { HandCoins, Loader2, Phone, Check } from "lucide-react";
import { motion } from "framer-motion";

export default function PublicCollector() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ amount: "", note: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/public/collector/${token}`); setData(data); }
    catch (e) { setError(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [token]);

  const openPay = (c) => { setActive(c); setForm({ amount: String(c.balance), note: "" }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/public/collector/${token}/payment`, { customer_id: active.id, amount: parseFloat(form.amount), note: form.note });
      toast.success("Pagamento registrado!");
      setOpen(false);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (error) return <div className="min-h-screen flex items-center justify-center p-6 bg-[#F9F8F6]"><p className="text-slate-500 text-center">{error}</p></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  return (
    <div className="min-h-screen bg-[#F9F8F6] py-8 px-4">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-2 mb-2"><HandCoins className="w-6 h-6 text-slate-900" /><p className="font-display font-bold text-slate-900">Cobrança — {data.store_name}</p></div>
        <div className="rounded-2xl border border-red-100 bg-red-50 p-5 mb-6">
          <p className="text-red-700/80 text-sm font-medium">Total a receber</p>
          <p className="font-display text-4xl font-black text-red-600">{brl(data.total_outstanding)}</p>
        </div>

        <div className="space-y-3" data-testid="collector-customer-list">
          {data.customers.length === 0 && <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-400">Ninguém devendo no momento 🎉</div>}
          {data.customers.map((c) => (
            <motion.div key={c.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{c.name}</p>
                  {c.phone && <p className="text-sm text-slate-400 flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{c.phone}</p>}
                  {c.note && <p className="text-sm text-slate-400 truncate">{c.note}</p>}
                </div>
                <span className="font-display font-bold text-lg text-red-600 shrink-0">{brl(c.balance)}</span>
              </div>
              <Button onClick={() => openPay(c)} className="w-full rounded-full font-semibold mt-3 bg-green-600 hover:bg-green-700" style={{ height: 48 }} data-testid={`register-payment-${c.id}`}><Check className="w-4 h-4" /> Registrar pagamento</Button>
            </motion.div>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">Pagamento — {active?.name}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div><Label>Valor recebido (R$)</Label><Input data-testid="collector-amount-input" type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required className="mt-1.5 text-lg" style={{ height: 52 }} /></div>
            <div><Label>Observação</Label><Input data-testid="collector-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5" style={{ height: 48 }} placeholder="Opcional" /></div>
            <DialogFooter><Button data-testid="collector-save-button" disabled={saving} className="w-full rounded-full font-semibold bg-green-600 hover:bg-green-700" style={{ height: 48 }}>{saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Confirmar pagamento"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
